import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useServices } from '../contexts/services/ServiceContext';
import SEO from '../components/SEO';

function Promotion() {
  const navigate = useNavigate();
  const { services } = useServices();

  const promoService = services.find(s => s.name.includes('Revitalizing Hydration'));

  const handleBookNow = () => {
    if (!promoService) return;
    localStorage.setItem('selectedService', JSON.stringify({
      id: promoService.id,
      name: promoService.name,
      duration: promoService.numericDuration || 0,
      price: promoService.numericPrice || 0,
      category_id: promoService.category_id
    }));
    navigate('/booking');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F8F6F2] via-[#E8F5F2] to-[#F9E9E9] font-sans">
      <SEO
        title="Spa Promotions & Special Offers | Healing Lotus Dallas"
        description="Exclusive spa promotions at Healing Lotus Skin Spa in Dallas, TX. Save on signature facials, massages, and wellness treatments — limited-time offers."
        path="/promotion"
      />
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-[#232347]/50 z-10"></div>
        <div className="relative z-20 text-center px-4">
          <span className="inline-block px-5 py-2 bg-[#C41E3A] text-white text-sm font-bold rounded-full mb-6 animate-pulse shadow-lg">
            LIMITED TIME OFFER
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 font-serif">
            Buy 1 Get 1 <span className="text-[#F7CAC9]">FREE</span>
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            LA MER The Revitalizing Hydration Facial
          </p>
        </div>
        <div className="absolute inset-0 z-0">
          <img
            src="/images/lamer.jpeg"
            alt="LA MER The Revitalizing Hydration Facial"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      <div className="container mx-auto py-12 md:py-20 px-4 md:px-6">
        <div className="max-w-4xl mx-auto">

          {/* Promotion Details Card */}
          <section className="mb-12">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-[#C9A96E]/20">
              <div className="bg-gradient-to-r from-[#F9F3E3] to-[#FDF8EF] px-8 py-6 border-b border-[#C9A96E]/20">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#C9A96E] animate-pulse"></div>
                  <span className="text-[#C9A96E] font-semibold tracking-wide uppercase text-sm">Open Spa Activity</span>
                </div>
              </div>

              <div className="p-8 md:p-12">
                <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#232347] mb-6">
                  LA MER The Revitalizing Hydration Facial
                </h2>

                <p className="text-[#232347]/80 text-lg leading-relaxed mb-6">
                  An immersive rejuvenating ritual powered by La Mer's legendary Miracle Broth™. This deeply restorative treatment infuses intense hydration, soothes stressed skin, and awakens a radiant, supple, and revitalized complexion.
                </p>
                <p className="text-[#232347]/70 leading-relaxed mb-8">
                  All La Mer facial treatments include specialized eye care.
                </p>

                {/* Price & Duration */}
                <div className="flex flex-wrap items-center gap-6 mb-10 pb-8 border-b border-gray-100">
                  <div>
                    <p className="text-sm text-[#232347]/50 mb-1">Price</p>
                    <p className="text-3xl font-bold text-[#232347]">
                      ${promoService?.numericPrice || promoService?.price || '239.90'}
                    </p>
                  </div>
                  <div className="w-px h-12 bg-gray-200"></div>
                  <div>
                    <p className="text-sm text-[#232347]/50 mb-1">Duration</p>
                    <p className="text-3xl font-bold text-[#232347]">
                      {promoService?.numericDuration || promoService?.duration || 80} min
                    </p>
                  </div>
                  <div className="w-px h-12 bg-gray-200"></div>
                  <div>
                    <p className="text-sm text-[#232347]/50 mb-1">You Get</p>
                    <p className="text-3xl font-bold text-[#C41E3A]">2 Sessions</p>
                  </div>
                </div>

                {/* Promotion Highlight Box */}
                <div className="bg-gradient-to-br from-[#C41E3A]/5 to-[#C41E3A]/10 border border-[#C41E3A]/20 rounded-xl p-6 md:p-8 mb-10">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[#C41E3A] rounded-full flex items-center justify-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-xl font-serif font-bold text-[#C41E3A] mb-2">
                        Buy 1, Get 1 FREE
                      </h3>
                      <p className="text-[#232347]/80 leading-relaxed">
                        Book one LA MER Revitalizing Hydration Facial and receive a second session <strong>completely free</strong>. Treat yourself to the ultimate skincare experience — twice the luxury, twice the glow.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Terms & Conditions */}
                <div className="bg-[#F8F6F2] rounded-xl p-6 md:p-8 mb-10">
                  <h3 className="text-lg font-serif font-semibold text-[#232347] mb-4 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#C9A96E]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Promotion Details
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 bg-[#C9A96E]/20 rounded-full flex items-center justify-center mt-0.5">
                        <span className="w-2 h-2 bg-[#C9A96E] rounded-full"></span>
                      </span>
                      <span className="text-[#232347]/80">
                        <strong>For personal use only</strong> — the complimentary session must be used by the same person who books the original appointment. It is non-transferable.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 bg-[#C9A96E]/20 rounded-full flex items-center justify-center mt-0.5">
                        <span className="w-2 h-2 bg-[#C9A96E] rounded-full"></span>
                      </span>
                      <span className="text-[#232347]/80">
                        <strong>Limited time promotion</strong> — this offer is available for a limited period only and may end without prior notice.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 bg-[#C9A96E]/20 rounded-full flex items-center justify-center mt-0.5">
                        <span className="w-2 h-2 bg-[#C9A96E] rounded-full"></span>
                      </span>
                      <span className="text-[#232347]/80">
                        <strong>Open spa activity</strong> — available to all guests, no membership required.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 bg-[#C9A96E]/20 rounded-full flex items-center justify-center mt-0.5">
                        <span className="w-2 h-2 bg-[#C9A96E] rounded-full"></span>
                      </span>
                      <span className="text-[#232347]/80">
                        The free session will be scheduled at a mutually convenient time after your first visit.
                      </span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-5 h-5 bg-[#C9A96E]/20 rounded-full flex items-center justify-center mt-0.5">
                        <span className="w-2 h-2 bg-[#C9A96E] rounded-full"></span>
                      </span>
                      <span className="text-[#232347]/80">
                        Cannot be combined with other promotions, discounts, or membership benefits.
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Urgency Banner */}
                <div className="bg-[#232347] rounded-xl p-6 md:p-8 mb-10 text-center">
                  <p className="text-white/60 text-sm uppercase tracking-widest mb-2">Don't miss out</p>
                  <p className="text-white text-2xl md:text-3xl font-serif font-bold mb-2">
                    Spots Are Filling Up Fast
                  </p>
                  <p className="text-white/70">
                    This exclusive promotion is available while slots last. Secure your appointment today.
                  </p>
                </div>

                {/* CTA */}
                <div className="text-center">
                  <button
                    onClick={handleBookNow}
                    className="inline-flex items-center bg-[#C9A96E] hover:bg-[#B8954F] text-white px-12 py-4 rounded-xl font-semibold text-xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    Book Now — Claim Your Free Session
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 ml-3" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <p className="text-[#232347]/50 text-sm mt-4">
                    You'll be taken to our booking page to select your preferred date and time.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Back link */}
          <div className="text-center">
            <Link to="/" className="text-[#232347]/60 hover:text-[#232347] transition-colors text-sm">
              &larr; Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Promotion;
